import { Component, OnInit, OnDestroy, ViewChild, ElementRef, PLATFORM_ID, Inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CustomerAdminService } from '../../../_services/api/customer-admin.service';
import { CustomerDetail, CustomerCreate, CustomerUpdate } from '../../../_models/customer.model';
import { ApiResponse } from '../../../_models/api-response.model';
import { NotificationService } from '../../../_services/app/notification.service';
import { Address } from '../../../_models/address.model';

@Component({
  selector: 'app-admin-member-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './admin-member-form.html',
  styleUrl: './admin-member-form.scss',
})
export class AdminMemberForm implements OnInit, OnDestroy {
  customerForm: FormGroup;
  addressForm: FormGroup;
  isEditMode = false;
  customerId?: number;
  isLoading = false;
  isSubmitting = false;
  showSuccess = false;
  successMessage = '';
  emailAlreadyExists = false;
  
  // For file upload (profile image)
  selectedFile: File | null = null;
  previewUrl: string | null = null;
  
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private customerService: CustomerAdminService,
    private route: ActivatedRoute,
    private router: Router,
    private notification: NotificationService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.addressForm = this.fb.group({
      streetNumber: ['', [Validators.required, Validators.maxLength(200)]],
      street: ['', Validators.maxLength(200)],
      city: ['', [Validators.required, Validators.maxLength(100)]],
      postalCode: ['', [Validators.required, Validators.pattern(/^[0-9]{4,10}$/)]],
    });

    this.customerForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      lastName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^[\d\s\+\-\(\)]{8,20}$/)]],
      companyName: ['', Validators.maxLength(200)],
      vatNumber: ['', Validators.maxLength(50)],
      address: this.addressForm
    });
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.customerId = +params['id'];
        this.customerForm.get('email')?.clearValidators();
        this.customerForm.get('email')?.updateValueAndValidity();
        this.loadCustomerDetails();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCustomerDetails(): void {
    if (!this.customerId) return;
    
    this.isLoading = true;
    this.customerService.getCustomerDetails(this.customerId).subscribe({
      next: (response: ApiResponse<CustomerDetail>) => {
        const customer = response.data;
        if (customer.address) {
          this.addressForm.patchValue(customer.address);
        }
        this.customerForm.patchValue({
          firstName: customer.firstName,
          lastName: customer.lastName,
          email: customer.email,
          phoneNumber: customer.phoneNumber,
          companyName: customer.companyName || '',
          vatNumber: customer.vatNumber || '',
        });
        if (customer.profileImage) {
          this.previewUrl = customer.profileImage;
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement du client:', error);
        this.notification.showError('Erreur lors du chargement du client');
        this.isLoading = false;
        this.router.navigate(['/admin/clients']);
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      if (!file.type.startsWith('image/')) {
        this.notification.showError('Veuillez sélectionner une image valide (JPG, PNG, GIF)');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        this.notification.showError('L\'image ne doit pas dépasser 5MB');
        return;
      }
      
      this.selectedFile = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewUrl = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  removeFile(): void {
    this.selectedFile = null;
    this.previewUrl = null;
  }

  onSubmit(): void {
    this.emailAlreadyExists = false;
    this.markFormGroupTouched(this.customerForm);
    this.markFormGroupTouched(this.addressForm);
    
    if (this.customerForm.invalid || this.addressForm.invalid) {
      this.notification.showError('Veuillez corriger les erreurs dans le formulaire');
      this.scrollToFirstError();
      return;
    }

    this.isSubmitting = true;
    
    if (this.isEditMode && this.customerId) {
      this.updateCustomer();
    } else {
      this.createCustomer();
    }
  }

  private createCustomer(): void {
    const formValue = this.customerForm.value;
    const address: Address = {
      streetNumber: formValue.address.streetNumber,
      street: formValue.address.street,
      city: formValue.address.city,
      postalCode: formValue.address.postalCode
    };
    const customerData: CustomerCreate = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      email: formValue.email,
      phoneNumber: formValue.phoneNumber,
      //password: formValue.password,
      companyName: formValue.companyName || undefined,
      vatNumber: formValue.vatNumber || undefined,
      address: address
    };

    this.customerService.createCustomer(customerData)
      .subscribe({
        next: (response: ApiResponse<CustomerDetail>) => {
          this.showSuccess = true;
          this.successMessage = 'Client créé avec succès! Un email a été envoyé avec les instructions de connexion.';
          this.notification.showSuccess('Client créé avec succès');
          this.resetForm();
          
          setTimeout(() => {
            this.isSubmitting = false;
            if (isPlatformBrowser(this.platformId)) {
              window.scrollTo({top: 0, behavior: 'smooth'});
            }
          }, 500);
        },
        error: (error) => {
          console.error('Erreur lors de la création du client:', error);
          this.isSubmitting = false;
          
          if (error.status === 409) {
            if (error.error?.message?.includes('Email')) {
              this.emailAlreadyExists = true;
              this.notification.showError('Cet email est déjà utilisé');
            }
            this.scrollToInput();
          } else {
            this.notification.showError('Erreur lors de la création du client');
          }
        }
      });
  }

  private updateCustomer(): void {
    if (!this.customerId) return;
    const formValue = this.customerForm.value;
    const address: Address | undefined = formValue.address.streetNumber ? {
      streetNumber: formValue.address.streetNumber,
      street: formValue.address.street,
      city: formValue.address.city,
      postalCode: formValue.address.postalCode
    } : undefined;

    const customerData: CustomerUpdate = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      email: formValue.email,
      phoneNumber: formValue.phoneNumber,
      companyName: formValue.companyName || undefined,
      vatNumber: formValue.vatNumber || undefined,
      address: address
    };

    Object.keys(customerData).forEach(key => {
      if (customerData[key as keyof CustomerUpdate] === undefined) {
        delete customerData[key as keyof CustomerUpdate];
      }
    });

    this.customerService.updateCustomer(this.customerId, customerData)
      .subscribe({
        next: (response: ApiResponse<CustomerDetail>) => {          
          this.notification.showSuccess('Client modifié avec succès');
          this.router.navigate(['/admin/clients/details', this.customerId]);
        },
        error: (error) => {
          console.error('Erreur lors de la modification du client:', error);
          this.isSubmitting = false;
          
          if (error.status === 409) {
            this.emailAlreadyExists = true;
            this.notification.showError('Cet email est déjà utilisé par un autre client');
            this.scrollToInput();
          } else {
            this.notification.showError('Erreur lors de la modification du client');
          }
        }
      });
  }

  private resetForm(): void {
    this.customerForm.reset({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      companyName: '',
      vatNumber: ''
    });
    
    this.addressForm.reset({
      streetNumber: '',
      street: '',
      city: '',
      postalCode: '',
    });
    
    this.selectedFile = null;
    this.previewUrl = null;
    this.customerForm.markAsPristine();
    this.customerForm.markAsUntouched();
    this.addressForm.markAsPristine();
    this.addressForm.markAsUntouched();
  }

  createAnother(): void {
    this.showSuccess = false;
    this.resetForm();
  }

  goToList(): void {
    this.router.navigate(['/admin/clients']);
  }

  @ViewChild('firstError') firstErrorElement!: ElementRef;
  scrollToFirstError() {
    if (this.firstErrorElement) {
      this.firstErrorElement.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        this.firstErrorElement.nativeElement.focus();
      }, 200);
    }
  }

  scrollToInput() {
    if (this.emailAlreadyExists) {
      if (isPlatformBrowser(this.platformId)) {
        const emailInput = document.getElementById('email');
        emailInput?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        emailInput?.focus();
      }
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  get firstName() { return this.customerForm.get('firstName'); }
  get lastName() { return this.customerForm.get('lastName'); }
  get email() { return this.customerForm.get('email'); }
  get phoneNumber() { return this.customerForm.get('phoneNumber'); }
  get companyName() { return this.customerForm.get('companyName'); }
  get vatNumber() { return this.customerForm.get('vatNumber'); }

  get streetNumber() { return this.addressForm.get('streetNumber'); }
  get street() { return this.addressForm.get('street'); }
  get city() { return this.addressForm.get('city'); }
  get postalCode() { return this.addressForm.get('postalCode'); }
}